import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { UserWithRoles, AssignableRole } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trash2, KeyRound, Plus, X } from "lucide-react";

const roleLabels: Record<string, string> = {
  super_admin: "Super Admin",
  org_admin: "Org Admin",
  school_admin: "School Admin",
  coach: "Coach",
  parent: "Parent",
  player: "Player",
  sponsor_admin: "Sponsor Admin",
  sponsor_user: "Sponsor User",
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);

  // Add role state -- driven entirely by server response
  const [showAddRole, setShowAddRole] = useState(false);
  const [assignableRoles, setAssignableRoles] = useState<AssignableRole[]>([]);
  const [addRoleType, setAddRoleType] = useState("");
  const [addScopeId, setAddScopeId] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  function loadUser() {
    if (!id) return;
    api
      .getUser(id)
      .then((res) => setUser(res))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load user"),
      )
      .finally(() => setLoading(false));
    // Also refresh assignable roles
    api.getAssignableRoles(id)
      .then((res) => setAssignableRoles(res.assignable_roles))
      .catch(() => setAssignableRoles([]));
  }

  useEffect(() => {
    loadUser();
  }, [id]);

  function openAddRole() {
    if (!id || assignableRoles.length === 0) return;
    setShowAddRole(true);
    setAddError(null);
    const first = assignableRoles[0];
    setAddRoleType(first.role_type);
    setAddScopeId(first.scopes.length === 1 ? first.scopes[0].id : "");
  }

  function closeAddRole() {
    setShowAddRole(false);
    setAddError(null);
    setAddRoleType("");
    setAddScopeId("");
  }

  // When role type changes, update scope selection
  function handleRoleTypeChange(roleType: string) {
    setAddRoleType(roleType);
    const role = assignableRoles.find((r) => r.role_type === roleType);
    if (role && role.scopes.length === 1) {
      setAddScopeId(role.scopes[0].id);
    } else {
      setAddScopeId("");
    }
  }

  const selectedRole = assignableRoles.find((r) => r.role_type === addRoleType);

  async function handleAddRole() {
    if (!user || !selectedRole) return;
    setAddError(null);

    const scopeId = selectedRole.scope_type === "platform" ? "platform" : addScopeId;
    if (!scopeId) {
      setAddError("Please select a scope");
      return;
    }

    setAddSubmitting(true);
    try {
      await api.createUser({
        email: user.email,
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        role_type: selectedRole.role_type as "super_admin" | "org_admin" | "school_admin" | "coach" | "parent" | "player" | "sponsor_admin" | "sponsor_user",
        scope_type: selectedRole.scope_type as "platform" | "organization" | "school" | "sponsor",
        scope_id: scopeId,
      });
      closeAddRole();
      loadUser();
    } catch (e) {
      setAddError(e instanceof api.ApiError ? e.message : "Failed to add role");
    } finally {
      setAddSubmitting(false);
    }
  }

  async function handleRemoveRole(roleId: string) {
    if (!id || !confirm("Remove this role?")) return;
    try {
      await api.removeUserRole(id, roleId);
      loadUser();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to remove role");
    }
  }

  async function handleResetPassword() {
    if (!id) return;
    setResetSubmitting(true);
    setResetResult(null);
    try {
      const result = await api.resetUserPassword(id);
      setResetResult(result.temporary_password);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!id || !confirm("Delete this user? This action cannot be undone.")) return;
    try {
      await api.deleteUser(id);
      navigate("/users");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete user");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="user-detail" data-state="loading">Loading...</p>;
  if (error && !user) return <p className="text-sm text-destructive" data-page="user-detail" data-state="error" data-error>{error}</p>;
  if (!user) return <p className="text-muted-foreground" data-page="user-detail" data-state="empty">User not found</p>;

  const displayName =
    user.first_name || user.last_name
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : user.email;

  return (
    <div className="flex flex-col gap-6" data-page="user-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 w-fit text-muted-foreground"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-semibold">{displayName}</h1>
          <p className="text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {user._actions.can_edit && (
            <Button
              variant="outline"
              disabled={resetSubmitting}
              onClick={handleResetPassword}
              data-action="reset-password"
            >
              <KeyRound className="mr-2 h-4 w-4" />
              {resetSubmitting ? "Resetting..." : "Reset Password"}
            </Button>
          )}
          {user._actions.can_delete && (
            <Button variant="destructive" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {resetResult && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <p className="font-medium text-green-800 dark:text-green-200">
                Password reset successfully!
              </p>
              <div className="rounded-md bg-white p-3 dark:bg-gray-900">
                <p className="text-sm text-muted-foreground">New temporary password:</p>
                <p className="font-mono text-lg font-bold">{resetResult}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Share this with the user securely. They should change it on first login.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-1 w-fit"
                onClick={() => setResetResult(null)}
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-region="info">
        <CardHeader>
          <CardTitle className="text-base">User Information</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Email</dt>
              <dd className="mt-1">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="mt-1">
                {user.first_name || user.last_name
                  ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                  : "--"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Email Verified</dt>
              <dd className="mt-1">
                <Badge variant={user.email_verified ? "default" : "secondary"}>
                  {user.email_verified ? "Verified" : "Unverified"}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1">{new Date(user.created_at).toLocaleDateString()}</dd>
            </div>
            {user.context?.team_count !== undefined && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Teams</dt>
                <dd className="mt-1">{user.context.team_count}</dd>
              </div>
            )}
            {user.context?.child_count !== undefined && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Linked Children</dt>
                <dd className="mt-1">{user.context.child_count}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card data-region="roles">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Roles</CardTitle>
          {user._actions.can_edit && !showAddRole && (
            <span title={assignableRoles.length === 0 ? "No additional roles available to assign" : undefined}>
              <Button
                size="sm"
                variant="outline"
                disabled={assignableRoles.length === 0}
                onClick={openAddRole}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Role
              </Button>
            </span>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {showAddRole && (
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="add-role-type">Role</Label>
                      <select
                        id="add-role-type"
                        aria-label="Role"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={addRoleType}
                        onChange={(e) => handleRoleTypeChange(e.target.value)}
                      >
                        {assignableRoles.map((r) => (
                          <option key={r.role_type} value={r.role_type}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedRole && selectedRole.scope_type !== "platform" && (
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="add-scope-id">
                          {selectedRole.scope_type === "organization" ? "Organization" : selectedRole.scope_type === "school" ? "School" : "Sponsor"}
                        </Label>
                        <select
                          id="add-scope-id"
                          aria-label="Scope"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={addScopeId}
                          onChange={(e) => setAddScopeId(e.target.value)}
                        >
                          {selectedRole.scopes.length !== 1 && <option value="">Select...</option>}
                          {selectedRole.scopes.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {addError && <p className="text-sm text-destructive">{addError}</p>}

                  <div className="flex gap-2">
                    <Button size="sm" disabled={addSubmitting} onClick={handleAddRole}>
                      {addSubmitting ? "Adding..." : "Add Role"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={closeAddRole}>
                      <X className="mr-1 h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {user.roles.length === 0 && (
            <p className="text-sm text-muted-foreground">No roles assigned</p>
          )}
          {user.roles.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user.roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">
                        {roleLabels[role.role_type] ?? role.role_type}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {role.scope_type === "platform" ? "Platform" : role.scope_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(role.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRole(role.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
